import React, { useEffect, useState } from "react";
import { useDisclosure } from "@mantine/hooks";
import {
  Drawer,
  Group,
  Text,
  ActionIcon,
  Indicator,
  Title,
  ScrollArea,
  Badge,
  UnstyledButton,
  Paper,
  ThemeIcon,
  Stack,
  Button,
  Divider,
  SimpleGrid,
} from "@mantine/core";
import {
  IconBell,
  IconTrash,
  IconInfoCircle,
  IconAlertCircle,
  IconClock,
  IconCheck,
  IconTrashX,
  IconFileInvoice,
  IconListCheck,
  IconCalendarEvent,
} from "@tabler/icons-react";
import { Link } from "react-router-dom";
import {
  query,
  where,
  orderBy,
  onSnapshot,
  collectionGroup,
  getDocs,
  limit,
  startAfter,
  writeBatch,
  updateDoc,
  doc,
  collection,
  arrayUnion,
  deleteDoc,
} from "firebase/firestore";
import { auth, db } from "../../../firebase/FirebaseCredential";
import { useAppSelector } from "../../../hooks/hooks";
import { RootState } from "../../../store/store";

// Type for each notification item
interface NotificationItem {
  id: string;
  title: string;
  message: string;
  type: string;
  read_by?: string[];
  task_link?: string;
}

// Utility to get color based on notification type
const getColorByType = (type: string): string => {
  switch (type) {
    case "alert":
      return "red";
    case "reminder":
      return "blue";
    case "system_update":
      return "yellow";
    default:
      return "indigo";
  }
};

const getIconByType = (type: string) => {
  switch (type) {
    case "alert":
      return <IconAlertCircle size={18} />;
    case "reminder":
      return <IconClock size={18} />;
    case "system_update":
      return <IconInfoCircle size={18} />;
    default:
      return <IconCheck size={18} />;
  }
};

// Header component for drawer
const NotificationHeader: React.FC<{ onClearAll?: () => void }> = ({
  onClearAll,
}) => (
  <Group position="apart" w="100%">
    <Group spacing="xs">
      <ThemeIcon variant="light" color="blue" size="md" radius="xl">
        <IconBell size={18} />
      </ThemeIcon>
      <Title order={4}>Notifications</Title>
    </Group>
    <Button variant="subtle" color="red" size="xs" compact onClick={onClearAll}>
      Clear all
    </Button>
  </Group>
);

const NotificationDrawer: React.FC = () => {
  const [opened, { open, close }] = useDisclosure(false);
  const companyId = useAppSelector(
    (state: RootState) => state.session?.company_id,
  );
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [lastVisible, setLastVisible] = useState<any | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [initialLimit] = useState(10);

  // Static Counts State
  const [openInvoicesCount, setOpenInvoicesCount] = useState(0);
  const [pendingTasksCount, setPendingTasksCount] = useState(0);
  const [todayTasksCount, setTodayTasksCount] = useState(0);
  const { branchesList } = useAppSelector((state: any) => state.branchList);

  const fetchStaticCounts = async () => {
    const user = auth.currentUser;
    if (!user || !companyId) return;

    try {
      // 1. Open Invoices
      const invoiceQuery = query(
        collection(db, "invoice"),
        where("company_id", "==", companyId),
        where("status", "in", ["open", "pending"]),
      );
      const invSnapshot = await getDocs(invoiceQuery);
      setOpenInvoicesCount(invSnapshot.size);

      // 2. Pending and Today's Tasks
      if (branchesList && branchesList.length > 0) {
        let totalPending = 0;
        let totalToday = 0;

        const endOfToday = new Date();
        endOfToday.setHours(23, 59, 59, 999);
        const endOfTodaySeconds = Math.floor(endOfToday.getTime() / 1000);

        const promises = branchesList.map(async (branch: any) => {
          const tasksRef = collection(db, "branch", branch.id, "tasks");
          const q = query(
            tasksRef,
            where("company_id", "==", companyId),
            where("status", "==", "pending"),
            where("assigned_to", "==", user.uid),
          );
          const snap = await getDocs(q);

          let pendingCount = snap.size;
          let todayCount = 0;

          snap.forEach((doc) => {
            const data = doc.data();
            if (!data.due_date || data.due_date.seconds <= endOfTodaySeconds) {
              todayCount++;
            }
          });

          return { pendingCount, todayCount };
        });

        const results = await Promise.all(promises);
        results.forEach((res) => {
          totalPending += res.pendingCount;
          totalToday += res.todayCount;
        });

        setPendingTasksCount(totalPending);
        setTodayTasksCount(totalToday);
      }
    } catch (e) {
      console.error("Error fetching static counts", e);
    }
  };

  useEffect(() => {
    if (opened && companyId) {
      fetchStaticCounts();
    }
  }, [opened, companyId]);

  // Setup real-time listener for the most recent notifications
  const setupNotificationsListener = () => {
    const user = auth.currentUser;
    if (!user || !companyId) return;

    // Create a query for the initial batch
    const q = query(
      collection(db, "companyprofile", companyId, "companynotifications"),
      where("visible_to", "array-contains-any", ["all_admins", user.uid]),
      orderBy("created_at", "desc"),
      limit(initialLimit),
    );

    // Set up the listener
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const updatedNotifications = snapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            // we attach a client-side 'seen' property for the UI
            seen: data.read_by?.includes(user.uid) || false,
          };
        }) as (NotificationItem & { seen: boolean })[];

        setNotifications(updatedNotifications);

        if (snapshot.docs.length > 0) {
          setLastVisible(snapshot.docs[snapshot.docs.length - 1]);
        }

        if (snapshot.docs.length < initialLimit) setHasMore(false);
        else setHasMore(true);
      },
      (error) => {
        console.log("Error in notifications listener:", error);
      },
    );

    return unsubscribe;
  };

  // Function to load more notifications (pagination)
  const loadMoreNotifications = async () => {
    const user = auth.currentUser;
    if (!user || !companyId || !lastVisible || loadingMore || !hasMore) return;

    setLoadingMore(true);

    try {
      const q = query(
        collection(db, "companyprofile", companyId, "companynotifications"),
        where("visible_to", "array-contains-any", ["all_admins", user.uid]),
        orderBy("created_at", "desc"),
        startAfter(lastVisible),
        limit(initialLimit),
      );

      const snapshot = await getDocs(q);

      const newNotifications = snapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          seen: data.read_by?.includes(user.uid) || false,
        };
      }) as (NotificationItem & { seen: boolean })[];

      // Append new notifications to existing ones
      setNotifications((prev) => [...prev, ...newNotifications]);

      // Update pagination controls
      if (snapshot.docs.length > 0) {
        setLastVisible(snapshot.docs[snapshot.docs.length - 1]);
      }

      if (snapshot.docs.length < initialLimit) {
        setHasMore(false);
      }
    } catch (error) {
      console.error("Error loading more notifications:", error);
    } finally {
      setLoadingMore(false);
    }
  };

  // Use this in your component's useEffect
  useEffect(() => {
    setHasMore(true); // Reset pagination when companyId changes
    setLastVisible(null);

    const unsubscribeNotifications = setupNotificationsListener();

    // Clean up listener when component unmounts or companyId changes
    return () => {
      if (unsubscribeNotifications) {
        unsubscribeNotifications();
      }
    };
  }, [companyId]);

  const clearAllNotifications = async () => {
    if (!companyId || notifications.length === 0) return;

    try {
      const batch = writeBatch(db);
      notifications.forEach((notif: any) => {
        const docRef = doc(
          db,
          "companyprofile",
          companyId,
          "companynotifications",
          notif.id,
        );
        batch.delete(docRef);
      });
      await batch.commit();
      console.log("All notifications deleted");
    } catch (error) {
      console.error("Error deleting all notifications:", error);
    }
  };

  const markAsRead = async (id: string) => {
    console.log(id, "mark As Read");
    const user = auth.currentUser;
    if (!user || !companyId) return;

    try {
      const docRef = doc(
        db,
        "companyprofile",
        companyId,
        "companynotifications",
        id,
      );

      await updateDoc(docRef, {
        read_by: arrayUnion(user.uid),
      });

      console.log("Notification marked as read");
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  const deleteNotification = async (notificationId: string) => {
    if (!companyId) return;
    try {
      const docRef = doc(
        db,
        "companyprofile",
        companyId,
        "companynotifications",
        notificationId,
      );
      await deleteDoc(docRef);
    } catch (error) {
      console.error("Error deleting notification:", error);
    }
  };

  const unreadCount = notifications.filter((n: any) => !n.seen).length;

  return (
    <>
      <Drawer
        opened={opened}
        onClose={close}
        title={<NotificationHeader onClearAll={clearAllNotifications} />}
        position="right"
        closeButtonProps={{ iconSize: 32 }}
        size="md"
        padding="xs"
      >
        <ScrollArea scrollbarSize={8} style={{ height: "calc(100vh - 60px)" }}>
          {/* STATIC NOTIFICATIONS OVERVIEW */}
          <Stack spacing="sm" mb="lg">
            <Text size="xs" weight={600} color="dimmed" tt="uppercase">
              Overview
            </Text>

            <SimpleGrid cols={2} spacing="sm">
              {/* Open Invoices */}
              <Paper shadow="xs" p="sm" radius="md" withBorder>
                <Group position="apart" align="flex-start" mb="xs">
                  <ThemeIcon variant="light" color="red" size="md" radius="xl">
                    <IconFileInvoice size={18} />
                  </ThemeIcon>
                  <Text weight={700} size="xl">
                    {openInvoicesCount}
                  </Text>
                </Group>
                <Text size="xs" weight={500} color="dimmed">
                  Open Invoices
                </Text>
              </Paper>

              {/* My Pending Tasks */}
              <Paper shadow="xs" p="sm" radius="md" withBorder>
                <Group position="apart" align="flex-start" mb="xs">
                  <ThemeIcon variant="light" color="blue" size="md" radius="xl">
                    <IconListCheck size={18} />
                  </ThemeIcon>
                  <Text weight={700} size="xl">
                    {pendingTasksCount}
                  </Text>
                </Group>
                <Text size="xs" weight={500} color="dimmed">
                  My Tasks
                </Text>
              </Paper>

              {/* Due Today */}
              <Paper
                shadow="xs"
                p="sm"
                radius="md"
                withBorder
                style={{
                  gridColumn: "span 2",
                }}
              >
                <Group position="apart" align="flex-start" mb="xs">
                  <ThemeIcon
                    variant="light"
                    color="yellow"
                    size="md"
                    radius="xl"
                  >
                    <IconCalendarEvent size={18} />
                  </ThemeIcon>
                  <Text weight={700} size="xl">
                    {todayTasksCount}
                  </Text>
                </Group>
                <Text size="xs" weight={500} color="dimmed">
                  My Tasks Due Today
                </Text>
              </Paper>
            </SimpleGrid>
          </Stack>

          <Divider mb="lg" variant="dashed" />

          <Text size="xs" weight={600} color="dimmed" tt="uppercase" mb="sm">
            Recent Alerts
          </Text>

          {notifications.length > 0 ? (
            <Stack spacing="sm">
              {notifications.map((notif: any) => (
                <Paper
                  key={notif.id}
                  shadow="sm"
                  p="md"
                  radius="md"
                  withBorder
                  style={{
                    backgroundColor: notif.seen ? "#ffffff" : "#f0f7ff",
                    transition: "transform 0.2s ease, box-shadow 0.2s ease",
                    cursor: "pointer",
                    position: "relative",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow =
                      "0 4px 12px rgba(0,0,0,0.08)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "none";
                    e.currentTarget.style.boxShadow =
                      "0 1px 3px rgba(0,0,0,0.05)";
                  }}
                >
                  <Group position="apart" align="flex-start" noWrap>
                    <Group spacing="sm" noWrap>
                      <ThemeIcon
                        variant={notif.seen ? "light" : "filled"}
                        color={getColorByType(notif.type)}
                        size="lg"
                        radius="md"
                      >
                        {getIconByType(notif.type)}
                      </ThemeIcon>
                      <div style={{ flex: 1 }}>
                        <Text
                          weight={600}
                          size="sm"
                          color={notif.seen ? "dimmed" : "dark"}
                        >
                          {notif.title}
                        </Text>
                        <Text
                          size="xs"
                          color="dimmed"
                          mt={2}
                          style={{ lineHeight: 1.4 }}
                        >
                          {notif.message}
                        </Text>
                      </div>
                    </Group>

                    <Group spacing="xs" noWrap>
                      <Badge
                        variant="dot"
                        color={getColorByType(notif.type)}
                        size="sm"
                      >
                        {notif?.type?.toUpperCase() || "INFO"}
                      </Badge>
                      <ActionIcon
                        color="red"
                        variant="subtle"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteNotification(notif.id);
                        }}
                      >
                        <IconTrashX size={16} />
                      </ActionIcon>
                    </Group>
                  </Group>

                  {/* Mark as read removed as per request */}
                </Paper>
              ))}

              {hasMore && (
                <Group position="center" mt="md" mb="xl">
                  <Button
                    variant="light"
                    onClick={loadMoreNotifications}
                    loading={loadingMore}
                  >
                    Load Older Notifications
                  </Button>
                </Group>
              )}
            </Stack>
          ) : (
            <Stack align="center" mt="xl" spacing="sm">
              <ThemeIcon size={64} radius="100%" variant="light" color="gray">
                <IconBell size={32} />
              </ThemeIcon>
              <Text weight={500} size="lg" color="dimmed">
                All caught up!
              </Text>
              <Text size="sm" color="dimmed" align="center">
                You don't have any notifications right now.
              </Text>
            </Stack>
          )}
        </ScrollArea>
      </Drawer>

      <Group position="center">
        <ActionIcon onClick={open}>
          <Indicator disabled={unreadCount === 0} size={10} offset={5}>
            <IconBell size={24} color="#ee374d" />
          </Indicator>
        </ActionIcon>
      </Group>
    </>
  );
};

const NotificationReceiver = () => {
  return <NotificationDrawer />;
};

export default NotificationReceiver;
